import React from "react"
import { Button, Modal, Typography } from "antd"
import { Logo } from "components/image/logo"
import { formatDate, formatMoney, maskCNPJ } from "utils";
import { businessName, businessDocument, businessAddress, businessAddressLine2, businessContact } from "config/business";

const { Text } = Typography;

const HEADER_STYLE = { padding: "10px 5px", textAlign: "center", fontSize: "10px", color: "#fff" };
const CELL_STYLE = { padding: "8px 5px", fontSize: "10px" };

const STATUS_LABELS = {
    C: "Concluída",
    F: "Falhou",
    U: "Em verificação",
    P: "Em processamento"
};

const NEUTRAL_COLOR = "#8c8c8c";

const isCompleted = (line) => !line.status || line.status.code === "C";

const chunkLines = (lines, chunkSize) => {
    const pages = [];
    for (let i = 0; i < lines.length; i += chunkSize) {
        pages.push(lines.slice(i, i + chunkSize));
    }
    return pages;
};

const InternalTransferReport = ({ data, componentRef, printShow, setPrintShow, handlePrint, sender }) => {
    const lines = React.useMemo(() => data?.lines || [], [data]);
    const showStatus = !!data?.showStatus;
    const columns = showStatus ? 6 : 4;
    const pages = React.useMemo(() => chunkLines(lines, 22), [lines]);

    return (
        <Modal
            width={1000}
            open={printShow}
            title="Relatório de transferências"
            onCancel={() => setPrintShow(false)}
            footer={[
                <Button key="submit" type="primary" disabled={lines.length === 0} onClick={handlePrint}>
                    Imprimir
                </Button>
            ]}
        >
            {lines.length === 0 && <Text type="secondary">Nenhuma transferência no período.</Text>}
            {lines.length > 0 &&
                <div ref={componentRef} className="print" style={{ alignItems: "center", justifyContent: "center", flexDirection: "column", display: "flex", padding: "30px" }}>
                    <div style={{ alignItems: "center", justifyContent: "center", flexDirection: "column", display: "flex", width: "100%" }} className="print-content">
                        <table style={{ width: "100%", marginBottom: "40px", borderCollapse: "collapse" }}>
                            <tbody>
                                <tr>
                                    <td style={{ width: "50%", fontSize: "12px", color: "#666" }} className="smallInfos">
                                        <Text className="black">{formatDate(new Date())}</Text>
                                    </td>
                                    <td style={{ width: "50%", textAlign: "right", fontSize: "12px", fontWeight: "bold" }}>
                                        <Text className="black">Aly+</Text>
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{ width: "50%", fontSize: "12px", color: "#666" }} className="smallInfos">{"Emitido por: " + sender}</td>
                                </tr>
                                <tr>
                                    <td colSpan={2} style={{ paddingTop: "40px" }}>
                                        <Typography.Title level={2} style={{ margin: 0, fontSize: "24px" }}>
                                            Relatório de transferências
                                        </Typography.Title>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={2} style={{ paddingTop: "8px" }}>
                                        <Typography.Title style={{ margin: 0, fontSize: "18px", fontWeight: "normal" }} level={3}>
                                            {data?.company?.fantasyName || ""}
                                        </Typography.Title>
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={2} style={{ paddingTop: "8px", fontSize: "12px" }}>
                                        {`CNPJ: ${data?.company?.cnpj ? maskCNPJ(data.company.cnpj) : ""}`}
                                        {data?.balance !== null && data?.balance !== undefined && ` | Saldo atual: ${formatMoney(data.balance)}`}
                                        {` | Período: ${data?.period || "Todo o período"}`}
                                        {data?.status && ` | Status: ${data.status}`}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        {pages.map((pageLines, pageIndex) => (
                            <React.Fragment key={pageIndex}>
                                {pageIndex > 0 && <div style={{ pageBreakBefore: "always", marginTop: "25px" }} />}
                                <div className="table-detail" style={{ marginTop: 25 }}>
                                    <table style={{ width: "100%", tableLayout: "fixed" }}>
                                        <thead>
                                            <tr>
                                                <th><Text style={HEADER_STYLE} strong>Data</Text></th>
                                                <th><Text style={HEADER_STYLE} strong>Valor Transferido</Text></th>
                                                <th><Text style={HEADER_STYLE} strong>Origem/Destino</Text></th>
                                                <th><Text style={HEADER_STYLE} strong>Tipo</Text></th>
                                                {showStatus && <th><Text style={HEADER_STYLE} strong>Status</Text></th>}
                                                {showStatus && <th><Text style={HEADER_STYLE} strong>Motivo</Text></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr style={{ height: "15px" }}>
                                                <td colSpan={columns} style={{ padding: 0 }}></td>
                                            </tr>
                                            {pageLines.map((line, index) => (
                                                <tr key={line.id || index} style={{ borderBottom: "1px solid #eee", pageBreakInside: "avoid" }}>
                                                    <td style={{ textAlign: "center" }}><Text style={CELL_STYLE}>{formatDate(line.createdAt)}</Text></td>
                                                    <td style={{ textAlign: "center" }}><Text style={{ ...CELL_STYLE, color: !isCompleted(line) ? NEUTRAL_COLOR : line.amount < 0 ? "red" : "" }}>{formatMoney(line.amount)}</Text></td>
                                                    <td style={{ textAlign: "center" }}><Text style={CELL_STYLE}>{line.counterpartName}</Text></td>
                                                    <td style={{ textAlign: "center" }}><Text style={{ ...CELL_STYLE, color: !isCompleted(line) ? NEUTRAL_COLOR : line.type === "SENT" ? "#ff4d4f" : "#52c41a" }}>{line.type === "SENT" ? "Transferido" : "Recebido"}</Text></td>
                                                    {showStatus && <td style={{ textAlign: "center" }}><Text style={CELL_STYLE}>{STATUS_LABELS[line.status?.code] || line.status?.name}</Text></td>}
                                                    {showStatus && <td style={{ textAlign: "center" }}><Text style={CELL_STYLE}>{line.errorMessage || ""}</Text></td>}
                                                </tr>
                                            ))}
                                            <tr style={{ height: "15px" }}>
                                                <td colSpan={columns} style={{ padding: 0 }}></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="print-footer" style={{ pageBreakInside: "avoid" }}>
                        <div style={{ marginBottom: "15px" }}>
                            <Logo justify="flex-start" />
                        </div>
                        <div style={{ width: "35%" }}></div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                            <span style={{ fontSize: "10px", fontWeight: "bold" }}>{businessName()}</span>
                            <span style={{ fontSize: "10px" }}><strong>CNPJ:</strong> {businessDocument()}</span>
                            <span style={{ fontSize: "10px" }}>{businessAddress()}</span>
                            <span style={{ fontSize: "10px" }}>{businessAddressLine2()}</span>
                            <span style={{ fontSize: "10px" }}>{businessContact()}</span>
                        </div>
                    </div>
                </div>
            }
        </Modal>
    )
}

export { InternalTransferReport }
